import React, { useEffect, useState } from 'react';
import { Container, Content, HeaderContent } from './styles';
import Button from '../../../components/Button';
import { physicalLocationsService } from '../../../services/physicalLocations.service';
import { PhysicalLocation } from '../../../interfaces/physicalLocations.interface';
import useToast from '../../../hooks/useToast';
import ModalListDocuments from "../../../components/Modals/ModalListDocuments";
import ModalCreateLocation from '../../../components/Modals/ModalCreateLocation';
import ModalLinkDocument from '../../../components/Modals/ModalLinkDocument';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

const PhysicalArchive: React.FC = () => {
  const [treeData, setTreeData] = useState<PhysicalLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalCreateOpen, setModalCreateOpen] = useState(false);
  const [modalLinkOpen, setModalLinkOpen] = useState(false);
  const [linkLocationId, setLinkLocationId] = useState('');
  
  const [modalListOpen, setModalListOpen] = useState(false);
  const [listLocationData, setListLocationData] = useState({ id: '', name: '' });
  
  const handleListDocuments = (id: string, name: string) => {
    setListLocationData({ id, name });
    setModalListOpen(true);
  };
  const { errorToast } = useToast();

  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(undefined);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});

  const toggleNode = (id: string) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchTree = async () => {
    try {
      setLoading(true);
      const data = await physicalLocationsService.getTree();
      setTreeData(data.data || []);
    } catch (err: any) {
      errorToast(err?.response?.data?.message || 'Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  const typeTranslations: Record<string, string> = {
    archive: 'Arquivo Central',
    zone: 'Zona',
    street: 'Rua/Corredor',
    bookshelf: 'Estante',
    face: 'Face',
    shelf: 'Prateleira',
    box: 'Caixa',
    folder: 'Pasta Física'
  };

  const handleCreateSublevel = (parentId: string) => {
    setSelectedParentId(parentId);
    setModalCreateOpen(true);
  };

  const handleCreateRoot = () => {
    setSelectedParentId(undefined);
    setModalCreateOpen(true);
  };

  const handleLinkDocument = (locationId: string) => {
    setLinkLocationId(locationId);
    setModalLinkOpen(true);
  };

  const renderTree = (nodes: PhysicalLocation[], level = 0) => {
    return (
      <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
        {nodes.map(node => {
          const hasChildren = node.children && node.children.length > 0;
          const isExpanded = !!expandedNodes[node._id];
          return (
            <li key={node._id} style={{ margin: '8px 0' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px',
                paddingLeft: `${15 + (level * 30)}px`,
                borderLeft: '4px solid #ccc', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px' 
              }}>
                <div style={{ color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {hasChildren ? (
                    <button 
                      onClick={() => toggleNode(node._id)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', width: '20px', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                    </button>
                  ) : (
                    <span style={{ width: '20px', display: 'inline-block' }}></span>
                  )}
                  <span>
                    <strong style={{ color: '#111', fontSize: '16px' }}>{node.name}</strong> - <span style={{color: '#555'}}>{typeTranslations[node.type] || node.type}</span> {node.capacity ? `(Capacidade: ${node.capacity})` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(node.type === 'box' || node.type === 'folder') && (
                    <>
                      <Button label="Ver Documentos" color="secondary" variantType="small" onClick={() => handleListDocuments(node._id, node.name)} />
                      <Button label="+ Documentos" color="secondary" variantType="small" onClick={() => handleLinkDocument(node._id)} />
                    </>
                  )}
                  <Button label="+ Subnível" color="success" variantType="small" onClick={() => handleCreateSublevel(node._id)} />
                  <Button label="Imprimir Etiqueta" color="primary" variantType="small" onClick={() => window.open(`/print/physical-qrcode/${node._id}`, '_blank')} />
                </div>
              </div>
              {hasChildren && isExpanded && renderTree(node.children!, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Container>
      <HeaderContent>
        <h2>Arquivo Físico</h2>
        <Button label="Adicionar Localização" color="primary" variantType="block" onClick={handleCreateRoot} />
      </HeaderContent>
      <Content>
        {loading ? (
          <p>Carregando...</p>
        ) : treeData.length > 0 ? (
          renderTree(treeData)
        ) : (
          <p>Nenhuma estrutura física cadastrada.</p>
        )}
      </Content>

      <ModalCreateLocation 
        isOpen={modalCreateOpen} 
        setIsOpen={setModalCreateOpen} 
        onSuccess={fetchTree}
        locations={treeData}
        initialParentId={selectedParentId}
      />

      <ModalLinkDocument
        isOpen={modalLinkOpen}
        setIsOpen={setModalLinkOpen}
        locationId={linkLocationId}
      />

      <ModalListDocuments 
        isOpen={modalListOpen} 
        setIsOpen={setModalListOpen} 
        locationId={listLocationData.id} 
        locationName={listLocationData.name} 
      />
    </Container>
  );
};

export default PhysicalArchive;
